import React, { useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Box,
    Paper,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

export default function PaginatedTable({
                                           fetchData,
                                           headers,
                                           renderRow,
                                           rowsPerPageOptions = [10, 25, 50, 100],
                                           onRowClick,
                                           toolbar = null,
                                       }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [page, setPage] = useState(parseInt(searchParams.get('page'), 10) || 0);
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const urlRows = parseInt(searchParams.get('rows'), 10);
        if (!isNaN(urlRows)) {
            return urlRows;
        }

        const localStorageRows = parseInt(localStorage.getItem('rowsPerPage'), 10);
        if (!isNaN(localStorageRows)) {
            return localStorageRows;
        }

        return 10; // Default value
    });
    const [searchText, setSearchText] = useState(searchParams.get('search') || '');
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const loadData = async (page, rowsPerPage, search) => {
        setIsLoading(true);
        try {
            const result = await fetchData(page, rowsPerPage, search);
            setData(result.data);
            setTotalCount(result.totalCount);
        }
        catch (error) {
            //console.error('Error fetching data:', error);
            setTotalCount(0);
        }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const defaultParams = {
            page: '0',
            rows: '10',
            search: '',
        };

        const updatedParams = {
            ...Object.fromEntries(searchParams),
            ...(page.toString() !== defaultParams.page && { page: page.toString() }),
            ...(rowsPerPage.toString() !== defaultParams.rows && { rows: rowsPerPage.toString() }),
            ...(searchText !== defaultParams.search && { search: searchText }),
        };

        setSearchParams(updatedParams);
    }, [page, rowsPerPage, searchText, setSearchParams]);

    useEffect(() => {
        const search = searchParams.get('search') || '';
        setSearchText(search);
    }, [searchParams]);

    useEffect(() => {
        localStorage.setItem('rowsPerPage', rowsPerPage);
    }, [rowsPerPage]);

    useEffect(() => {
        loadData(page, rowsPerPage, searchText);
    }, [page, rowsPerPage, searchText]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page when rows per page changes
    };

    return (
        <Box sx={{ width: '100%' }} component={Paper}>
            {toolbar && <Box sx={{ mb: 2 }}>{toolbar}</Box>}
            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((header, index) => (
                                <TableCell key={index}>{header}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={headers.length} align="center">
                                    Cargando...
                                </TableCell>
                            </TableRow>
                        ) : data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={headers.length} align="center">
                                    No se encontraron registros coincidentes
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => renderRow(row, onRowClick))
                        )}
                    </TableBody>
                </Table>
            </Box>
            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                labelRowsPerPage="Filas por página"
            />
        </Box>
    );
}
